import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Wallet, BedDouble, ArrowDownRight } from "lucide-react";

const studies = [
  { name: "Apollo", n: "1 016", sens: "96,7%", spec: "89,8%", npv: "99,4%" },
  { name: "Observer", n: "583", sens: "98,1%", spec: "88,4%", npv: "96,9%" },
  { name: "Pathfinder", n: "597", sens: "93,8%", spec: "89,8%", npv: "97,9%" },
  { name: "Autopilot", n: "1 140", sens: "85,6%", spec: "92,3%", npv: "99,0%" },
];

const economics = [
  {
    icon: ArrowDownRight,
    metric: "−24%",
    label: "PCR multiplex coûteux évités",
  },
  {
    icon: Wallet,
    metric: "−155 €",
    label: "Économies par patient (évaluation médico-éco.)",
  },
  {
    icon: BedDouble,
    metric: "−0,8 j",
    label: "Réduction de la durée de séjour",
  },
  {
    icon: TrendingUp,
    metric: "−44%",
    label: "Réduction du recours aux UHCD",
  },
];

const Performance = () => {
  return (
    <section
      id="performance"
      data-testid="performance-section"
      className="relative py-24 md:py-32"
    >
      <div className="pointer-events-none absolute top-20 right-[-10%] w-[420px] h-[420px] rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, rgba(91,196,209,0.30), transparent 70%)" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <motion.div
          className="max-w-2xl mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-xs uppercase tracking-[0.2em] text-[#5BC4D1] font-medium mb-4">
            Performance clinique
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight">
            Une précision diagnostique{" "}
            <span className="font-semibold">supérieure aux marqueurs classiques.</span>
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            MeMed BV® a été validé dans plus de 100 000 patients à travers
            6 études multicentriques internationales — surpassant
            constamment CRP et procalcitonine (PCT) en sensibilité,
            spécificité et valeur prédictive négative.
          </p>
        </motion.div>

        {/* Studies grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
        >
          {studies.map((s, i) => (
            <motion.div
              key={s.name}
              data-testid={`study-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: i * 0.08, duration: 0.7 }}
              className="bg-[#0E1530] border border-white/10 rounded-2xl p-6 hover:border-[#5BC4D1]/30 transition-colors"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="font-display text-xl font-medium text-white">
                  Étude {s.name}
                </div>
                <span className="text-[11px] tracking-wider text-slate-500 uppercase">
                  n = {s.n}
                </span>
              </div>
              <div className="space-y-3">
                {[
                  { k: "Sensibilité", v: s.sens },
                  { k: "Spécificité", v: s.spec },
                  { k: "VPN", v: s.npv },
                ].map((row) => (
                  <div
                    key={row.k}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-slate-400">{row.k}</span>
                    <span className="text-[#5BC4D1] font-semibold tracking-tight">
                      {row.v}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Health economics */}
        <motion.div
          className="mt-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9 }}
        >
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight max-w-xl">
              Impact{" "}
              <span className="font-semibold">médico-économique</span>{" "}
              démontré.
            </h3>
            <p className="text-sm text-slate-400 max-w-md leading-relaxed">
              Données issues d&apos;évaluations en conditions réelles
              (urgences, urgences pédiatriques, soins non programmés) [10,11].
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {economics.map((e, i) => {
              const Icon = e.icon;
              return (
                <motion.div
                  key={e.label}
                  data-testid={`economic-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: i * 0.08, duration: 0.6 }}
                  className="relative overflow-hidden bg-gradient-to-br from-[#0E1530] to-[#0A1128] border border-white/10 rounded-2xl p-6 hover:border-[#5BC4D1]/30 transition-colors group"
                >
                  <Icon
                    size={20}
                    strokeWidth={1.6}
                    className="text-[#5BC4D1] mb-4"
                  />
                  <div className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">
                    {e.metric}
                  </div>
                  <div className="text-xs text-slate-400 mt-2 leading-snug">
                    {e.label}
                  </div>
                  <div className="absolute -bottom-10 -right-10 w-32 h-32 rounded-full bg-[#5BC4D1]/5 group-hover:bg-[#5BC4D1]/10 transition-colors" />
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Performance;
